import { useState, useCallback, useRef, useEffect } from 'react';
import type { SortingState, VisibilityState, ColumnOrderState, ColumnSizingState } from '@tanstack/react-table';

interface TableState {
  sorting: SortingState;
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
  columnSizing: ColumnSizingState;
  pageSize: number;
}

const STORAGE_PREFIX = 'backoffice:table-state:';
const DEBOUNCE_MS = 500;

function loadState(tableName: string): Partial<TableState> {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${tableName}`);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as Partial<TableState>;
  } catch {
    return {};
  }
}

function saveState(tableName: string, state: TableState): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${tableName}`, JSON.stringify(state));
  } catch {
    // localStorage full or unavailable
  }
}

interface UseTableStateOptions {
  tableName: string;
  defaultSort?: SortingState;
  urlFilters?: Record<string, string>;
}

export function useTableState({ tableName, defaultSort, urlFilters }: UseTableStateOptions) {
  const saved = loadState(tableName);

  // Precedence: user's saved sort (if any) > per-table default > unsorted. This way defaults
  // apply on first visit but a user's explicit click persists across reloads.
  const [sorting, setSorting] = useState<SortingState>(saved.sorting ?? defaultSort ?? []);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(saved.columnVisibility ?? {});
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(saved.columnOrder ?? []);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(saved.columnSizing ?? {});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(saved.pageSize ?? 50);
  const [globalFilter, setGlobalFilter] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(urlFilters ?? {});

  // Reset state when navigating to a different table
  const prevTableRef = useRef(tableName);
  useEffect(() => {
    if (prevTableRef.current !== tableName) {
      prevTableRef.current = tableName;
      const newSaved = loadState(tableName);
      setSorting(newSaved.sorting ?? defaultSort ?? []);
      setColumnVisibility(newSaved.columnVisibility ?? {});
      setColumnOrder(newSaved.columnOrder ?? []);
      setColumnSizing(newSaved.columnSizing ?? {});
      setPageSize(newSaved.pageSize ?? 50);
      setPage(1);
      setGlobalFilter('');
      setActiveFilters(urlFilters ?? {});
    }
  }, [tableName, defaultSort, urlFilters]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      saveState(tableName, { sorting, columnVisibility, columnOrder, columnSizing, pageSize });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [tableName, sorting, columnVisibility, columnOrder, columnSizing, pageSize]);

  const clearFilters = useCallback(() => {
    setActiveFilters({});
    setGlobalFilter('');
    setPage(1);
  }, []);

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
    setPage(1);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  return {
    sorting,
    columnVisibility,
    columnOrder,
    columnSizing,
    page,
    pageSize,
    globalFilter,
    activeFilters,
    setSorting: handleSortingChange,
    setColumnVisibility,
    setColumnOrder,
    setColumnSizing,
    setPage,
    setPageSize: handlePageSizeChange,
    setGlobalFilter,
    setActiveFilters,
    clearFilters
  };
}
